import { AttachFile, DragIndicator, Send } from '@mui/icons-material';
import GradientAiIcon from 'components/icons/GradientAiIcon';
import {
  CircularProgress,
  Divider,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { UploadedFile } from 'components/Dropzone';
import { useAiAgent } from 'contexts/AiAgentContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import { UploadType } from 'further-types/files';
import { FileAttachment } from 'further-types/lp/ai-agent';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';
import { Column, Row } from 'further-ui/components/Layout';
import useFileUpload from 'hooks/ui/useFileUpload';
import api from 'lib/trpcClient';
import {
  createIncomeOrExpenseAddRoute,
  INCOME_OR_EXPENSE_LIST,
  LP_INVESTMENT_CREATE,
  LP_INVESTMENT_LIST,
  LP_REALISATION_CREATE,
  LP_REALISATION_LIST,
  LP_CAPITAL_ACTION_CREATE,
  LP_CAPITAL_ACTION_LIST,
  LP_MANUAL_ACTION_CREATE,
  LP_MANUAL_ACTION_LIST,
  COMMITMENTS,
  ACCOUNTING,
  BANK_RECONCILIATION,
  FUND_SETUP,
  LP_FIRM_SETTINGS,
  VEHICLES,
  LP_COMPANY_LIST,
  LP_COMPANY_ADD,
  EQUALISATIONS,
  EQUALISATIONS_ADD,
  PARTNER_TRANSFERS_LIST,
  PARTNER_TRANSFERS_CREATE,
  LP_PERIOD_END_REVIEW,
  LP_ONBOARDING,
  FEE_MANAGER,
  USER_MANAGEMENT,
} from 'adminConstants/routes';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

type NavigationRoute =
  | 'income_add'
  | 'expense_add'
  | 'income_expense_list'
  | 'investment_create'
  | 'investment_list'
  | 'realisation_create'
  | 'realisation_list'
  | 'capital_action_create'
  | 'capital_action_list'
  | 'manual_journal_create'
  | 'manual_journal_list'
  | 'commitments'
  | 'accounting'
  | 'bank_reconciliation'
  | 'fund_setup'
  | 'firm_settings'
  | 'vehicles'
  | 'holdings_list'
  | 'holdings_create'
  | 'equalisations'
  | 'equalisations_add'
  | 'partner_transfers'
  | 'partner_transfers_create'
  | 'period_end_review'
  | 'onboarding'
  | 'fee_manager'
  | 'user_management';

export type PendingInvoice = {
  base64: string;
  mediaType: FileAttachment['mediaType'];
  fileName: string;
  uploadedPath: string;
  uploadedName: string;
  uploadedPreview: string;
};

type ChatMessage =
  | { role: 'user'; content: string; fileName?: string }
  | { role: 'assistant'; content: string }
  | { role: 'action'; content: string };

// Set to true to close the agent panel after navigating to a new page
const CLOSE_ON_NAVIGATION = false;

const INITIAL_WIDTH = 480;
const INITIAL_HEIGHT = 620;
const MIN_WIDTH = 360;
const MIN_HEIGHT = 300;

// Outer shell — grey background, large rounded corners
const Shell = styled('div')(({ theme }) => ({
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1300,
  backgroundColor: theme.palette.grey[200],
  borderRadius: 20,
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 0 50px 0 rgba(0, 0, 0, 0.05)',
  userSelect: 'none',
}));

// White card — the main content area
const ContentCard = styled('div')(({ theme }) => ({
  borderRadius: 16,
  margin: theme.spacing(0, 1, 1),
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
}));

const CardHeader = styled(Row)(({ theme }) => ({
  margin: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  cursor: 'grab',
  flexShrink: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:active': { cursor: 'grabbing' },
}));

const MessagesArea = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  padding: theme.spacing(8),
  gap: theme.spacing(6),
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
}));

const IdleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  textAlign: 'center',
  padding: theme.spacing(2, 12),
}));

// User message — white card with border, text above, file below divider
const UserCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(3, 4),
  maxWidth: '88%',
  alignSelf: 'flex-end',
  color: theme.palette.grey[900],
}));

const FileDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
}));

// AI response — plain text, no bubble, no avatar
const AiText = styled(Typography)(({ theme }) => ({
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: 1.65,
  color: theme.palette.grey[900],
}));

// Tool / action row
const ActionRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(0.25, 0),
}));

// Input area
const InputSection = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 2),
  flexShrink: 0,
}));

const InputPill = styled('div', {
  shouldForwardProp: (p) => p !== 'isDragging',
})<{ isDragging: boolean }>(({ theme, isDragging }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: isDragging
    ? `${theme.palette.primary.main}12`
    : theme.palette.white,
  borderRadius: 30,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  gap: theme.spacing(0.5),
  transition: 'background-color 0.15s',
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.common.white,
  width: 34,
  height: 34,
  flexShrink: 0,
  '&:hover': { backgroundColor: theme.palette.grey[900] },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
}));

const ResizeHandle = styled('div')({
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: 18,
  height: 18,
  cursor: 'nwse-resize',
});

const renderMarkdown = (text: string): ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Unexpected FileReader result type'));
        return;
      }
      resolve(reader.result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getMediaType = (file: File): FileAttachment['mediaType'] | null => {
  const { type } = file;
  if (
    type === 'image/jpeg' ||
    type === 'image/png' ||
    type === 'image/gif' ||
    type === 'image/webp' ||
    type === 'application/pdf'
  ) {
    return type;
  }
  return null;
};

function resolveNavigationRoute(route: NavigationRoute): string {
  switch (route) {
    case 'income_add':
      return createIncomeOrExpenseAddRoute(IncomeOrExpenseType.Income);
    case 'expense_add':
      return createIncomeOrExpenseAddRoute(IncomeOrExpenseType.Expense);
    case 'income_expense_list':
      return INCOME_OR_EXPENSE_LIST;
    case 'investment_create':
      return LP_INVESTMENT_CREATE;
    case 'investment_list':
      return LP_INVESTMENT_LIST;
    case 'realisation_create':
      return LP_REALISATION_CREATE;
    case 'realisation_list':
      return LP_REALISATION_LIST;
    case 'capital_action_create':
      return LP_CAPITAL_ACTION_CREATE;
    case 'capital_action_list':
      return LP_CAPITAL_ACTION_LIST;
    case 'manual_journal_create':
      return LP_MANUAL_ACTION_CREATE;
    case 'manual_journal_list':
      return LP_MANUAL_ACTION_LIST;
    case 'commitments':
      return COMMITMENTS;
    case 'accounting':
      return ACCOUNTING;
    case 'bank_reconciliation':
      return BANK_RECONCILIATION;
    case 'fund_setup':
      return FUND_SETUP;
    case 'firm_settings':
      return LP_FIRM_SETTINGS;
    case 'vehicles':
      return VEHICLES;
    case 'holdings_list':
      return LP_COMPANY_LIST;
    case 'holdings_create':
      return LP_COMPANY_ADD;
    case 'equalisations':
      return EQUALISATIONS;
    case 'equalisations_add':
      return EQUALISATIONS_ADD;
    case 'partner_transfers':
      return PARTNER_TRANSFERS_LIST;
    case 'partner_transfers_create':
      return PARTNER_TRANSFERS_CREATE;
    case 'period_end_review':
      return LP_PERIOD_END_REVIEW;
    case 'onboarding':
      return LP_ONBOARDING;
    case 'fee_manager':
      return FEE_MANAGER;
    case 'user_management':
      return USER_MANAGEMENT;
  }
}

const AiAgent: React.FC = () => {
  const { isOpen, isMinimized, close, pendingNotification, clearNotification } =
    useAiAgent();
  const { selectedVehicleId } = useLayoutContext();
  const history = useHistory();

  const [messages, setMessages] = useState<Array<ChatMessage>>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [pos, setPos] = useState({
    x:
      typeof window !== 'undefined'
        ? window.innerWidth - INITIAL_WIDTH - 24
        : 100,
    y: 80,
  });
  const [size, setSize] = useState({
    width: INITIAL_WIDTH,
    height: INITIAL_HEIGHT,
  });

  useEffect(() => {
    if (!isOpen) {
      setPos({
        x: window.innerWidth - INITIAL_WIDTH - 24,
        y: 80,
      });
    }
  }, [isOpen]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Holds the pending invoice for the current file upload so navigation can use it
  const pendingInvoiceRef = useRef<PendingInvoice | null>(null);
  const dragState = useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);
  const resizeState = useRef<{
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null>(null);

  const { uploadFile } = useFileUpload();
  const chatMutation = api.aiAgent.chat.useMutation();

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  // Consume form-fill notifications pushed from elsewhere in the app
  useEffect(() => {
    if (pendingNotification) {
      setMessages((prev) => [
        ...prev,
        { role: 'action', content: pendingNotification },
      ]);
      clearNotification();
      scrollToBottom();
    }
  }, [pendingNotification, clearNotification, scrollToBottom]);

  const handleDragBarMouseDown = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLElement && e.target.closest('button')) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
    };
    const onMove = (e: MouseEvent) => {
      if (!dragState.current) return;
      setPos({
        x: dragState.current.startPosX + e.clientX - dragState.current.startX,
        y: dragState.current.startPosY + e.clientY - dragState.current.startY,
      });
    };
    const onUp = () => {
      dragState.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    resizeState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: size.width,
      startH: size.height,
    };
    const onMove = (e: MouseEvent) => {
      if (!resizeState.current) return;
      setSize({
        width: Math.max(
          MIN_WIDTH,
          resizeState.current.startW + e.clientX - resizeState.current.startX,
        ),
        height: Math.max(
          MIN_HEIGHT,
          resizeState.current.startH + e.clientY - resizeState.current.startY,
        ),
      });
    };
    const onUp = () => {
      resizeState.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const sendMessage = async (
    text: string,
    file?: FileAttachment,
    fileName?: string,
  ) => {
    if (!text.trim() && !file) return;
    if (!selectedVehicleId) return;

    const messageContent =
      text.trim() || (fileName ? 'Please process this file' : '');

    const newMessages: Array<ChatMessage> = [
      ...messages,
      { role: 'user', content: messageContent, fileName },
    ];
    setMessages(newMessages);
    setInputText('');
    setIsLoading(true);
    scrollToBottom();

    try {
      const apiMessages = newMessages
        .filter(
          (m): m is { role: 'user' | 'assistant'; content: string } =>
            m.role !== 'action',
        )
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await chatMutation.mutateAsync({
        vehicleId: selectedVehicleId,
        messages: apiMessages,
        file,
      });

      const next: Array<ChatMessage> = [
        ...newMessages,
        { role: 'assistant', content: response.message },
      ];

      if (response.navigation) {
        const pending = pendingInvoiceRef.current;
        const route = resolveNavigationRoute(response.navigation.route);
        next.push({
          role: 'action',
          content: `Redirecting you now…`,
        });
        setMessages(next);
        scrollToBottom();
        setTimeout(() => {
          if (
            pending &&
            (response.navigation!.route === 'income_add' ||
              response.navigation!.route === 'expense_add')
          ) {
            history.push(route, { pendingInvoice: pending });
          } else {
            history.push(route);
          }
          pendingInvoiceRef.current = null;
          if (CLOSE_ON_NAVIGATION) close();
        }, 800);
      } else {
        setMessages(next);
        scrollToBottom();
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const processFile = async (rawFile: File) => {
    if (isLoading) return;
    if (!selectedVehicleId) return;

    const mediaType = getMediaType(rawFile);
    if (!mediaType) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Please upload an image (JPEG, PNG, GIF, WebP) or PDF file.',
        },
      ]);
      return;
    }

    setIsLoading(true);
    scrollToBottom();

    try {
      const uploadedFile = Object.assign(rawFile, {
        path: rawFile.name,
      }) as UploadedFile;

      const [base64, uploadResult] = await Promise.all([
        fileToBase64(rawFile),
        uploadFile(uploadedFile, UploadType.LpTransactionAttachments),
      ]);

      if (!uploadResult.data) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Failed to upload the file. Please try again.',
          },
        ]);
        return;
      }

      pendingInvoiceRef.current = {
        base64,
        mediaType,
        fileName: rawFile.name,
        uploadedPath: uploadResult.data.filePath,
        uploadedName: uploadResult.data.name ?? rawFile.name,
        uploadedPreview: uploadResult.data.url,
      };

      await sendMessage(
        '',
        { base64, mediaType, fileName: rawFile.name },
        rawFile.name,
      );
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Failed to process the file. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) return;
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDropZoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDropZoneDragLeave = (e: React.DragEvent) => {
    if (
      !(
        e.relatedTarget instanceof Node &&
        e.currentTarget.contains(e.relatedTarget)
      )
    )
      setIsDragging(false);
  };
  const handleDropZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  if (!isOpen) return null;

  return (
    <Shell
      style={{
        left: pos.x,
        top: pos.y,
        width: size.width,
        height: isMinimized ? 'auto' : size.height,
      }}
    >
      {!isMinimized && (
        <ContentCard
          onDragOver={handleDropZoneDragOver}
          onDragLeave={handleDropZoneDragLeave}
          onDrop={handleDropZoneDrop}
        >
          {/* Card header */}
          <CardHeader
            justify="space-between"
            centered
            onMouseDown={handleDragBarMouseDown}
          >
            <Row centered spacing="xs">
              <GradientAiIcon size={20} />
              <Typography variant="h6">AI Agent</Typography>
            </Row>
            <DragIndicator fontSize="small" />
          </CardHeader>

          {/* Messages */}
          <MessagesArea>
            {messages.length === 0 && !isLoading && (
              <Column centered spacing="xs" justify="center" fullHeight>
                <GradientAiIcon size={32} grey />
                <IdleText variant="body2">
                  Ask me about functions available within the platform, or drop
                  an invoice below — I can help with processing income and
                  expenses.
                </IdleText>
              </Column>
            )}

            {messages.map((message, index) => {
              if (message.role === 'user') {
                return (
                  <Row key={index} justify="flex-end">
                    <UserCard>
                      <Typography
                        variant="body2"
                        color="grey.900"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {message.content}
                      </Typography>
                      {message.fileName && (
                        <>
                          <FileDivider />
                          <Row centered spacing="xs">
                            <AttachFile
                              sx={{ fontSize: 16, color: 'text.secondary' }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: 13 }}
                            >
                              {message.fileName}
                            </Typography>
                          </Row>
                        </>
                      )}
                    </UserCard>
                  </Row>
                );
              }

              if (message.role === 'action') {
                return (
                  <ActionRow key={index} justify="space-between" centered>
                    <Row centered spacing="xs">
                      <GradientAiIcon size={16} style={{ opacity: 0.4 }} />
                      <Typography variant="body2" color="text.disabled">
                        {message.content}
                      </Typography>
                    </Row>
                  </ActionRow>
                );
              }

              // assistant
              return (
                <AiText key={index} variant="body2">
                  {renderMarkdown(message.content)}
                </AiText>
              );
            })}

            {isLoading && (
              <Row centered spacing="xs">
                <CircularProgress size={14} sx={{ color: 'grey.900' }} />
                <Typography variant="body2" color="text.secondary">
                  Thinking…
                </Typography>
              </Row>
            )}
            <div ref={messagesEndRef} />
          </MessagesArea>

          {/* Input */}
          <InputSection>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            <InputPill isDragging={isDragging}>
              <Tooltip title="Attach file">
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                >
                  <AttachFile
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                  />
                </IconButton>
              </Tooltip>
              <InputBase
                fullWidth
                placeholder={
                  isDragging ? 'Drop file here…' : 'Chat with AI Agent'
                }
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(inputText);
                  }
                }}
                disabled={isLoading}
                sx={{ fontSize: 14 }}
              />
              <SendButton
                size="small"
                onClick={() => sendMessage(inputText)}
                disabled={isLoading || !inputText.trim()}
              >
                <Send
                  sx={{
                    fontSize: 16,
                    color:
                      isLoading || !inputText.trim()
                        ? undefined
                        : 'common.white',
                  }}
                />
              </SendButton>
            </InputPill>
          </InputSection>
        </ContentCard>
      )}

      <ResizeHandle onMouseDown={handleResizeMouseDown} />
    </Shell>
  );
};

export default AiAgent;
